import { Component, Input, OnChanges, OnInit, ViewChildren } from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { ClientNotificationHandlerService, TimeSlot } from '@thehubz/client/core';
import { Availability, UpdateUserDto, User } from '@thehubz/client/data-access-api';
import { DEFAULT_WORK_END_TIME, DEFAULT_WORK_START_TIME } from '@thehubz/shared/utils';
import { WEEK_DAYS } from '../constants/week-days';
import { WeeklyAvailability } from '../models';
import { UserProfileService } from '../user-profile.service';
import { DayAvailabilitySelectorComponent } from './day-availability-selector/day-availability-selector.component';

@Component({
  selector: 'thehubz-user-availability',
  templateUrl: './user-availability.component.html',
  styleUrls: ['./user-availability.component.scss'],
})
export class UserAvailabilityComponent implements OnInit, OnChanges {
  @Input() userData!: User;
  @Input() isOtherUser = false;
  @Input() userId?: string;
  editMode = false;

  /**
   * Week days
   */
  weekDays = WEEK_DAYS;

  /**
   * User availability form group
   */
  availabilitiesForm!: FormGroup;

  @ViewChildren(DayAvailabilitySelectorComponent) dayAvailabilitySelectors!: DayAvailabilitySelectorComponent[];

  constructor(
    private userProfileService: UserProfileService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private fb: NonNullableFormBuilder,
  ) {}

  ngOnInit() {
    this.availabilitiesForm = this._userAvailabilityForm();
  }

  ngOnChanges(): void {
    if (this.userData) {
      this.patchValues();
    }
  }

  patchValues() {
    const availabilities = this._getUserDailyAvailability(this.userData.profile.availabilities as Availability);
    this.availabilitiesForm.patchValue({
      mon: availabilities.mon || this._getDefaultAvailability(),
      tue: availabilities.tue || this._getDefaultAvailability(),
      wed: availabilities.wed || this._getDefaultAvailability(),
      thu: availabilities.thu || this._getDefaultAvailability(),
      fri: availabilities.fri || this._getDefaultAvailability(),
      sat: availabilities.sat || this._getDefaultAvailability(),
      sun: availabilities.sun || this._getDefaultAvailability(),
    });
  }

  /**
   * Call when user clicks on save button
   */
  saveAvailability() {
    this.editMode = true;
    if (this.userId) {
      const userObject: UpdateUserDto = {
        profile: {
          availabilities: this._getAvailabilityFromForm(),
        },
      };
      this.userProfileService.updateUser(this.userId, userObject, 'availability').subscribe((response) => {
        if (response) {
          this.clientNotificationHandlerService.successNotification($localize`User availability updated`);
          this.editMode = false;
        }
      });
    }
  }

  /**
   * Apply time slots to all the selected days
   */
  applyTimeSlotsSelectedDays(timeSlots: TimeSlot[]) {
    this.weekDays.forEach((day) => {
      const control = this.availabilitiesForm.get(day.key);
      if (control?.value.selected) {
        control.patchValue({
          selected: true,
          timeSlots,
        });
      }
    });
  }

  /**
   * Close all day availability selector components (set expanded to false)
   */
  closeAllDayAvailabilitySelector() {
    this.dayAvailabilitySelectors.forEach((dayAvailabilitySelector: DayAvailabilitySelectorComponent) => {
      dayAvailabilitySelector.expanded = false;
    });
  }

  /**
   * Get weekly availability from form data
   */
  private _getAvailabilityFromForm(): Availability {
    const availability: Availability = {
      mon: this.availabilitiesForm.value['mon'] ? this.availabilitiesForm.value['mon'].timeSlots : [],
      tue: this.availabilitiesForm.value['tue'] ? this.availabilitiesForm.value['tue'].timeSlots : [],
      wed: this.availabilitiesForm.value['wed'] ? this.availabilitiesForm.value['wed'].timeSlots : [],
      thu: this.availabilitiesForm.value['thu'] ? this.availabilitiesForm.value['thu'].timeSlots : [],
      fri: this.availabilitiesForm.value['fri'] ? this.availabilitiesForm.value['fri'].timeSlots : [],
      sat: this.availabilitiesForm.value['sat'] ? this.availabilitiesForm.value['sat'].timeSlots : [],
      sun: this.availabilitiesForm.value['sun'] ? this.availabilitiesForm.value['sun'].timeSlots : [],
    };
    return availability;
  }

  /**
   * Get user availability form group
   */
  private _userAvailabilityForm(): FormGroup {
    // form group definition
    const availabilityFormGroup = this.fb.group({});

    // add controls to form group
    this.weekDays.forEach((day) => {
      const formControl = this.fb.control({
        selected: day.selected,
        timeSlots: [] as TimeSlot[],
      });

      if (day.selected) {
        formControl.patchValue({
          selected: day.selected,
          timeSlots: [
            {
              start: DEFAULT_WORK_START_TIME,
              end: DEFAULT_WORK_END_TIME,
            },
          ],
        });
      }

      availabilityFormGroup.addControl(day.key, formControl);
    });

    return availabilityFormGroup;
  }

  /**
   * Get empty availability object
   */
  private _getDefaultAvailability() {
    return {
      selected: false,
      timeSlots: [],
    };
  }

  /**
   * Get user daily availability from user profile
   *
   * @param availabilities
   */
  private _getUserDailyAvailability(availabilities: Availability): WeeklyAvailability {
    if (!availabilities) {
      return {};
    }
    const weeklyAvailability: WeeklyAvailability = {};

    this.weekDays.forEach((day) => {
      if (availabilities[day.key as keyof Availability]?.length) {
        weeklyAvailability[day.key] = {
          selected: true,
          timeSlots: availabilities[day.key],
        };
      } else {
        weeklyAvailability[day.key] = this._getDefaultAvailability();
      }
    });

    return weeklyAvailability;
  }
}
